import { DataTable as DataTableRenderer } from '@/components/data-table/DataTable';
import { PaginationFooter } from '@/components/data-table/PaginationFooter';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  matchesSearchWords,
  normalizeFields,
  searchWords,
} from '@/lib/search';
import { useDebouncedValue } from '@mantine/hooks';
import type { Order } from '@octave/contracts';
import {
  ColumnDef,
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

interface DataTableProps {
  columns: ColumnDef<Order>[];
  data: Order[];
}

/**
 * Searched fields: order number, organization, client (first and last
 * name, either order) and article names, ignoring case and accents. Ids
 * and descriptions stay out: short words would match them at random.
 */
function orderSearchFields(order: Order): (string | null | undefined)[] {
  return [
    order.orderNumber,
    order.organization.name,
    order.createdBy?.firstName,
    order.createdBy?.lastName,
    ...order.items.map((item) => item.name),
  ];
}

export function DataTable({ columns, data }: DataTableProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(() => searchParams.get('q') ?? '');
  const [debouncedSearch] = useDebouncedValue(search, 300);

  // The input stays local (a URL-controlled input lags behind typing); the
  // URL follows, so a search survives a round trip to an order.
  useEffect(() => {
    setSearchParams(
      (previous) => {
        const params = new URLSearchParams(previous);
        if (debouncedSearch) params.set('q', debouncedSearch);
        else params.delete('q');
        return params;
      },
      { replace: true },
    );
  }, [debouncedSearch, setSearchParams]);

  const indexed = useMemo(
    () =>
      data.map((order) => ({
        order,
        fields: normalizeFields(orderSearchFields(order)),
      })),
    [data],
  );
  const filteredData = useMemo(() => {
    const words = searchWords(debouncedSearch);
    return indexed
      .filter(({ fields }) => matchesSearchWords(fields, words))
      .map(({ order }) => order);
  }, [indexed, debouncedSearch]);

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    initialState: {
      pagination: {
        pageSize: 10,
        pageIndex: 0,
      },
      sorting,
      columnFilters,
    },
    state: {
      sorting,
      columnFilters,
    },
  });

  const rowCount = table.getFilteredRowModel().rows.length;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <Input
          placeholder="Rechercher une commande, un client..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="max-w-sm"
        />
        <Select
          value={
            (table.getColumn('status')?.getFilterValue() as string) ?? 'all'
          }
          onValueChange={(value) =>
            table
              .getColumn('status')
              ?.setFilterValue(value === 'all' ? '' : value)
          }
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="pending">En cours</SelectItem>
            <SelectItem value="completed">Terminée</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <DataTableRenderer table={table} />
      <PaginationFooter
        page={table.getState().pagination.pageIndex + 1}
        pageCount={Math.max(1, table.getPageCount())}
        summary={`${rowCount} résultat${rowCount > 1 ? 's' : ''}`}
        pageSize={table.getState().pagination.pageSize}
        onPageSizeChange={(size) => table.setPageSize(size)}
        onPageChange={(page) => table.setPageIndex(page - 1)}
      />
    </div>
  );
}
